/** Editor для Звіту виробництва за зміну (Порція 4b).
 *
 * Розщеплені permissions (на відміну від інших editors):
 * - Save/Update = production.report.create
 * - Post/Unpost = production.report.post
 * - Delete = production.report.delete
 *
 * Multi-product 1A — НЕ блокуємо локально, backend POST → 422 з detail
 * (рішення власника: backend authoritative).
 *
 * 2 таблиці: products + materials.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { orgs, nomenclature as nomApi } from '@/api/catalogs';
import { refs as refsApi } from '@/api/refs';
import { departmentsApi } from '@/api/hr';
import { nomenclatureGroups } from '@/api/nomenclatureGroups';
import { accounts as accountsApi } from '@/api/documents';
import {
  productionReports,
  type ProductionReportProductIn,
  type ProductionReportMaterialIn,
  type ProductionReportReturnableWasteIn,
  type ProductionReportServiceIn,
} from '@/api/productionReports';
import { useNumberPreview } from '@/hooks/useNumberPreview';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import { usePermissions } from '@/auth/usePermissions';
import DocumentToolbar from '@/shared/components/DocumentToolbar';
import AttachmentsPanel from '@/shared/components/AttachmentsPanel';
import DocLayout from '@/shared/components/DocLayout';
import DocBlock, { type DocBlockState } from '@/shared/components/DocBlock';
import DocRail from '@/shared/components/DocRail';
import DatePopover from '@/components/DatePopover';
import { ProjectField } from '@/components/ProjectSelect';
import NomenclatureSelect from '@/components/NomenclatureSelect';
import NomenclaturePickerModal from '@/components/NomenclaturePickerModal';
import type { GoodsBalanceRow } from '@/api/documents';
import SearchSelect from '@/components/SearchSelect';
import { useFormDraft } from '@/shared/hooks/useFormDraft';
import TchTable from '@/shared/components/TchTable';

const PERMISSION_CREATE = 'production.report.create';
const PERMISSION_POST = 'production.report.post';
const PERMISSION_DELETE = 'production.report.delete';

interface FormState {
  organization_id: string;
  document_number: string;
  document_date: string;
  warehouse_id: string;
  department_id: string;
  comment: string;
  project_id: string | null;
  products: ProductionReportProductIn[];
  materials: ProductionReportMaterialIn[];
  returnable_waste: ProductionReportReturnableWasteIn[];
  services: ProductionReportServiceIn[];
}

const EMPTY_FORM: FormState = {
  organization_id: '',
  document_number: '',
  document_date: new Date().toISOString().slice(0, 16),
  warehouse_id: '',
  department_id: '',
  comment: '',
  project_id: null,
  products: [],
  materials: [],
  returnable_waste: [],
  services: [],
};

const newService = (n: number): ProductionReportServiceIn => ({
  line_number: n, nomenclature_id: '', quantity: '1',
});

function extractError(e: unknown): string {
  if (typeof e === 'object' && e && 'response' in e) {
    const r = (e as {
      response?: {
        data?: { detail?: string | Array<{ loc?: unknown[]; msg?: string }> }
      }
    }).response;
    const detail = r?.data?.detail;
    if (typeof detail === 'string') return detail;
    // Pydantic 422: detail — array {type, loc, msg, input, ctx}
    if (Array.isArray(detail) && detail.length > 0) {
      return detail.slice(0, 3).map((item) => {
        const field = Array.isArray(item.loc) && item.loc.length > 1
          ? String(item.loc[item.loc.length - 1]) : 'поле';
        return `${field}: ${item.msg ?? 'невідома помилка'}`;
      }).join('; ');
    }
    return 'Error';
  }
  return String(e);
}

export default function ProductionReportEditor() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const isNew = !id || id === 'new';

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const { availableOrgs, totalAccessibleOrgs } = usePermissionsByOrg(PERMISSION_CREATE);
  // Розщеплені permissions: save=create, post/unpost=post, delete=delete.
  const { has } = usePermissions();
  const canCreate = has(PERMISSION_CREATE);
  const canPost = has(PERMISSION_POST);
  const canDelete = has(PERMISSION_DELETE);
  const nomQ = useQuery({ queryKey: ['nomenclature-full'], queryFn: () => nomApi.listAll() });
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [error, setError] = useState<string | null>(null);
  const warehousesQ = useQuery({
    queryKey: ['warehouses', form.organization_id],
    queryFn: () => refsApi.warehouses(form.organization_id),
    enabled: !!form.organization_id,
  });
  const deptQ = useQuery({
    queryKey: ['departments', form.organization_id],
    queryFn: () => departmentsApi.list({ organization_id: form.organization_id, limit: 500 }),
    enabled: !!form.organization_id,
  });
  const groupQ = useQuery({
    queryKey: ['nomenclature-groups'],
    queryFn: () => nomenclatureGroups.list(),
  });
  // ч.660: одиниці виміру для колонки «Одиниця».
  const uomsQ = useQuery({ queryKey: ['uoms-for-picker'], queryFn: () => refsApi.uoms() });
  // План рахунків для вибору (щоб не вписувати руками — без помилок).
  const accountsQ = useQuery({
    queryKey: ['accounts-postable'],
    queryFn: () => accountsApi.list({ only_postable: true, limit: 1000 }),
  });
  const [tab, setTab] = useState<'products' | 'materials' | 'waste' | 'services' | 'accounts'>('products');
  // ч.660: підбір з попапу (мультивибір) для активної ТЧ.
  const [pickerFor, setPickerFor] = useState<'products' | 'materials' | 'waste' | null>(null);
  const docQuery = useQuery({
    queryKey: ['production-report', id],
    queryFn: () => productionReports.get(id!),
    enabled: !isNew,
  });

  // Чернетка незбережених правок (2026-08-24): живе у браузері 10 год,
  // у базу нічого не пишеться. Повернувся на документ — запропонує відновити.
  useFormDraft(form, setForm, { ready: isNew || !!docQuery.data });
  const numberPreview = useNumberPreview('doc.production_reports', form.organization_id, isNew);

  useEffect(() => {
    if (isNew && availableOrgs.length > 0 && !form.organization_id) {
      setForm((f) => ({ ...f, organization_id: availableOrgs[0]!.id }));
    }
  }, [isNew, availableOrgs, form.organization_id]);
  useEffect(() => {
    if (!isNew && docQuery.data) {
      const d = docQuery.data;
      setForm({
        organization_id: d.organization_id,
        document_number: d.document_number,
        document_date: d.document_date.slice(0, 16),
        warehouse_id: d.warehouse_id,
        department_id: d.department_id ?? '',
        comment: d.comment ?? '',
        project_id: d.project_id ?? null,
        products: d.products.map((p) => ({
          line_number: p.line_number,
          nomenclature_id: p.nomenclature_id,
          quantity: p.quantity,
          specification_id: p.specification_id,
          account_code: p.account_code,
          cost_account_code: p.cost_account_code,
          planned_cost: p.planned_cost,
          planned_amount: p.planned_amount,
          nomenclature_group_id: p.nomenclature_group_id,
        })),
        materials: d.materials.map((m) => ({
          line_number: m.line_number,
          nomenclature_id: m.nomenclature_id,
          quantity: m.quantity,
          account_code: m.account_code,
        })),
        returnable_waste: (d.returnable_waste ?? []).map((w) => ({
          line_number: w.line_number,
          nomenclature_id: w.nomenclature_id,
          quantity: w.quantity,
          price: w.price,
          amount: w.amount,
          account_code: w.account_code,
          nomenclature_group_id: w.nomenclature_group_id,
        })),
        services: (d.services ?? []).map((s) => ({
          line_number: s.line_number,
          nomenclature_id: s.nomenclature_id,
          quantity: s.quantity,
          amount: s.amount,
          nomenclature_group_id: s.nomenclature_group_id,
          comment: s.comment,
        })),
      });
    }
  }, [isNew, docQuery.data]);

  const isPosted = docQuery.data?.posted ?? false;
  // Режим обліку виробництва обраної орг: у Спрощеному ховаємо планові колонки.
  const selectedOrg = orgList.data?.items.find((o) => o.id === form.organization_id);
  const isWip = selectedOrg?.production_costing_mode === '1C_WIP';

  const buildPayload = () => ({
    document_number: form.document_number || null,
    document_date: new Date(form.document_date).toISOString(),
    warehouse_id: form.warehouse_id,
    department_id: form.department_id || null,
    comment: form.comment || null,
        project_id: form.project_id,
    products: form.products,
    materials: form.materials,
    returnable_waste: form.returnable_waste,
    services: form.services,
  });

  const create = useMutation({
    mutationFn: () => productionReports.create({ organization_id: form.organization_id, ...buildPayload() }),
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['production-reports'] });
      navigate(`/production-reports/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  const update = useMutation({
    mutationFn: () => productionReports.update(id!, buildPayload()),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['production-report', id] });
      qc.invalidateQueries({ queryKey: ['production-reports'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  // Новий: створити і одразу провести (create → post → картка).
  const createAndPost = useMutation({
    mutationFn: async () => {
      const doc = await productionReports.create({ organization_id: form.organization_id, ...buildPayload() });
      try {
        await productionReports.post(doc.id);
      } catch (e) {
        // ч.1343: документ уже створено — відкриваємо його, інакше повторне
        // «провести» створило б дубль (правило ч.1266).
        navigate(`/production-reports/${doc.id}`, { replace: true });
        throw e;
      }
      return doc;
    },
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['production-reports'] });
      navigate(`/production-reports/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  // Збережений непроведений: зберегти правки і одразу провести (update → post).
  const updateAndPost = useMutation({
    mutationFn: async () => {
      await productionReports.update(id!, buildPayload());
      await productionReports.post(id!);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['production-report', id] });
      qc.invalidateQueries({ queryKey: ['production-reports'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const unpost = useMutation({
    mutationFn: () => productionReports.unpost(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['production-report', id] });
      qc.invalidateQueries({ queryKey: ['production-reports'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const remove = useMutation({
    mutationFn: () => productionReports.remove(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['production-reports'] });
      navigate('/production-reports');
    },
    onError: (e) => setError(extractError(e)),
  });

  const uomShortById = useMemo(() => {
    const m = new Map<string, string>();
    (uomsQ.data ?? []).forEach((u) => m.set(u.id, u.short_name));
    return m;
  }, [uomsQ.data]);
  const uomOf = (nomenclatureId: string, uomId?: string | null): string => {
    if (uomId) return uomShortById.get(uomId) ?? '';
    const n = nomQ.data?.items.find((x) => x.id === nomenclatureId);
    return (n?.base_uom_id && uomShortById.get(n.base_uom_id)) || '';
  };
  // ч.660: підбір з попапу — рядки в активну ТЧ, одиниця з картки.
  function addRowsFromPicker(rows: GoodsBalanceRow[]) {
    const which = pickerFor;
    if (!which) return;
    setForm((f) => {
      // uom_id НЕ підставляємо (FK на ref.nomenclature_uoms, не класифікатор;
      // NULL = базова одиниця) — прецедент-фікс FK violation ч.671.
      if (which === 'products') {
        return { ...f, products: [...f.products, ...rows.map((r, k) => ({
          line_number: f.products.length + 1 + k, nomenclature_id: r.nomenclature_id,
          quantity: '1', uom_id: null }))] };
      }
      if (which === 'materials') {
        return { ...f, materials: [...f.materials, ...rows.map((r, k) => ({
          line_number: f.materials.length + 1 + k, nomenclature_id: r.nomenclature_id,
          quantity: '1', uom_id: null }))] };
      }
      return { ...f, returnable_waste: [...f.returnable_waste, ...rows.map((r, k) => ({
        line_number: f.returnable_waste.length + 1 + k, nomenclature_id: r.nomenclature_id,
        quantity: '1', amount: '0' }))] };
    });
  }
  function removeProduct(i: number) {
    setForm((f) => ({
      ...f,
      products: f.products.filter((_, idx) => idx !== i).map((p, idx) => ({ ...p, line_number: idx + 1 })),
    }));
  }
  function setProductField(i: number, patch: Partial<ProductionReportProductIn>) {
    setForm((f) => ({ ...f, products: f.products.map((p, idx) => (idx === i ? { ...p, ...patch } : p)) }));
  }
  function removeMaterial(i: number) {
    setForm((f) => ({
      ...f,
      materials: f.materials.filter((_, idx) => idx !== i).map((m, idx) => ({ ...m, line_number: idx + 1 })),
    }));
  }
  function setMaterialField(i: number, patch: Partial<ProductionReportMaterialIn>) {
    setForm((f) => ({ ...f, materials: f.materials.map((m, idx) => (idx === i ? { ...m, ...patch } : m)) }));
  }
  function removeWaste(i: number) {
    setForm((f) => ({
      ...f,
      returnable_waste: f.returnable_waste.filter((_, idx) => idx !== i).map((w, idx) => ({ ...w, line_number: idx + 1 })),
    }));
  }
  function setWasteField(i: number, patch: Partial<ProductionReportReturnableWasteIn>) {
    setForm((f) => ({ ...f, returnable_waste: f.returnable_waste.map((w, idx) => (idx === i ? { ...w, ...patch } : w)) }));
  }
  function addService() { setForm((f) => ({ ...f, services: [...f.services, newService(f.services.length + 1)] })); }
  function removeService(i: number) {
    setForm((f) => ({
      ...f,
      services: f.services.filter((_, idx) => idx !== i).map((s, idx) => ({ ...s, line_number: idx + 1 })),
    }));
  }
  function setServiceField(i: number, patch: Partial<ProductionReportServiceIn>) {
    setForm((f) => ({ ...f, services: f.services.map((s, idx) => (idx === i ? { ...s, ...patch } : s)) }));
  }

  if (!isNew && docQuery.isLoading) return <div>Завантаження…</div>;

  
  
  
  
  // Вибір рахунка з плану рахунків (замість ручного вводу — без помилок).
  const acctList = accountsQ.data ?? [];
  const nomName = (nid: string) => {
    const n = nomQ.data?.items.find((x) => x.id === nid);
    return n ? `${n.name}${n.code ? ' — ' + n.code : ''}` : '—';
  };
  const acctItems = acctList.map((a) => ({ id: a.code, label: `${a.code} — ${a.name}`, hint: null }));
  const acctSelect = (
    value: string | null | undefined,
    onChange: (v: string | null) => void,
    def: string,
  ) => (
    <SearchSelect items={acctItems} value={value ?? ''}
      onChange={(v) => onChange(v || null)} disabled={isPosted}
      placeholder={`— (деф. ${def}) —`} />
  );

  function validate(): string | null {
    if (!form.warehouse_id) return 'Оберіть склад';
    if (form.products.length === 0) return 'Додайте хоча б один рядок продукції';
    if (form.products.some((p) => !p.nomenclature_id))
      return 'Оберіть номенклатуру у всіх рядках продукції';
    return null;
  }

  // ч.911 (новий дизайн, Закон #10): «ще дій» у документа не було.
  const railActions: { label: string; title?: string; disabled?: boolean; onClick: () => void }[] = [];
  const blocks: DocBlockState[] = [
    { id: 'main', title: 'Основні дані', required: 2,
      filled: (form.organization_id ? 1 : 0) + (form.document_date ? 1 : 0) },
    { id: 'warehouse', title: 'Склад і підрозділ', required: 1,
      filled: form.warehouse_id ? 1 : 0 },
    { id: 'tables', title: 'Продукція і матеріали', required: 1,
      filled: form.products.length > 0 ? 1 : 0 },
    { id: 'extra', title: 'Коментарі та вкладення', required: 0, filled: 0 },
  ];
  // ч.911: кнопка додавання рядків активної ТЧ — у шапці блоку.
  const pickerTitles = {
    products: 'Підбір продукції (мультивибір)',
    materials: 'Підбір сировини (мультивибір)',
    waste: 'Підбір відходів (мультивибір)',
  } as const;
  const tableActions = isPosted || tab === 'accounts' ? undefined : tab === 'services' ? (
    <button type="button" className="primary" onClick={addService}>+ додати рядок</button>
  ) : (
    <button type="button" className="primary" onClick={() => setPickerFor(tab)}
      disabled={!form.organization_id}
      title={!form.organization_id ? 'Спершу оберіть юрособу' : pickerTitles[tab]}>
      + додати номенклатуру
    </button>
  );

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {isNew ? 'Новий звіт виробництва' : `Звіт виробництва: ${form.document_number}`}
        </h2>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', marginLeft: 'auto' }}>
        <DocumentToolbar
        entriesDoc={{ type: 'doc.production_reports', id: isNew ? '' : id! }}
          railMode
          extraActions={railActions}
          onBackToList={() => navigate('/production-reports')}
          onSave={() => {
            const err = validate();
            if (err) { setError(err); return; }
            setError(null);
            (isNew ? create.mutate() : update.mutate());
          }}
          saveDisabled={!canCreate || create.isPending || update.isPending}
          isPosted={isPosted}
          isNew={isNew}
          postable={canPost}
          onPost={() => {
            const err = validate();
            if (err) { setError(err); return; }
            setError(null);
            (isNew ? createAndPost.mutate() : updateAndPost.mutate());
          }}
          postPending={create.isPending || update.isPending || createAndPost.isPending || updateAndPost.isPending}
          postTitle="Зберігає поточні зміни та проводить документ"
          onUnpost={() => unpost.mutate()}
          unpostPending={unpost.isPending}
          onPrint={() => navigate(`/production-reports/${id}/print`)}
          deletable={canDelete}
          onDelete={() => remove.mutate()}
          deletePending={remove.isPending}
        />
        </div>
      </div>

      <DocLayout rail={
        <DocRail blocks={blocks} actions={railActions} isNew={isNew}
          doc={{ type: 'doc.production_reports', id: isNew ? '' : id! }} />
      }>
      {/* ч.660 (розкладка власника): [Юрособа|Номер|Дата] → [Склад|Підрозділ] → Коментар.
          ч.911 — блоками (коментар — в останньому блоці). */}
      <DocBlock index={1} title="Основні дані">
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Організація *</label>
          <select value={form.organization_id}
                  onChange={(e) => setForm({ ...form, organization_id: e.target.value })}
                  disabled={!isNew}>
            <option value="">—</option>
            {(isNew ? availableOrgs : orgList.data?.items ?? []).map((o) => (
              <option key={o.id} value={o.id}>{o.name} — {o.code}</option>
            ))}
          </select>
          {isNew && availableOrgs.length < totalAccessibleOrgs && (
            <small style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>
              Показано {availableOrgs.length} з {totalAccessibleOrgs} ваших організацій
            </small>
          )}
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>Номер</label>
          <input value={form.document_number}
                 onChange={(e) => setForm({ ...form, document_number: e.target.value })}
                 placeholder={isNew && numberPreview ? `буде: ${numberPreview}` : ''}
                 disabled={isPosted} />
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>Дата *</label>
          <DatePopover withTime value={form.document_date}
                 onChange={(v) => setForm({ ...form, document_date: v })} disabled={isPosted} />
        </div>
        <ProjectField span={2}
            value={form.project_id}
            organizationId={form.organization_id}
            onChange={(v) => setForm({ ...form, project_id: v })}
            disabled={isPosted} />
      </div>
      </DocBlock>

      <DocBlock index={2} title="Склад і підрозділ">
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 6' }}>
          <label>Склад *</label>
          <select value={form.warehouse_id}
                  onChange={(e) => setForm({ ...form, warehouse_id: e.target.value })} disabled={isPosted}>
            <option value="">—</option>
            {warehousesQ.data?.map((w) => (<option key={w.id} value={w.id}>{w.name} — {w.code}</option>))}
          </select>
        </div>
        <div className="field" style={{ gridColumn: 'span 6' }}>
          <label>Підрозділ</label>
          <select value={form.department_id}
                  onChange={(e) => setForm({ ...form, department_id: e.target.value })} disabled={isPosted}>
            <option value="">—</option>
            {deptQ.data?.items.map((d) => (<option key={d.id} value={d.id}>{d.name}</option>))}
          </select>
        </div>
      </div>
      </DocBlock>

      <DocBlock index={3} title="Продукція і матеріали" actions={tableActions}>
      {/* Табличні частини — вкладки (канон seg-tabs, «Рахунки обліку» справа) */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
        <div className="seg-tabs">
          <button type="button" className={tab === 'products' ? 'active' : ''}
            onClick={() => setTab('products')}>Продукція ({form.products.length})</button>
          <button type="button" className={tab === 'materials' ? 'active' : ''}
            onClick={() => setTab('materials')}>Матеріали ({form.materials.length})</button>
          <button type="button" className={tab === 'waste' ? 'active' : ''}
            onClick={() => setTab('waste')}>Поворотні відходи ({form.returnable_waste.length})</button>
          <button type="button" className={tab === 'services' ? 'active' : ''}
            onClick={() => setTab('services')}>Послуги ({form.services.length})</button>
        </div>
        <div className="seg-tabs" style={{ marginLeft: 'auto' }}>
          <button type="button" className={tab === 'accounts' ? 'active' : ''}
            onClick={() => setTab('accounts')}>Рахунки обліку</button>
        </div>
      </div>

      {tab === 'accounts' && (
        <div style={{ marginTop: '0.75rem' }}>
          <TchTable>
            <thead>
              <tr style={{ textTransform: 'none' }}>
                <th style={{ width: 120 }}>Розділ</th>
                <th>Номенклатура</th>
                <th style={{ width: 220 }}>Рах. обліку</th>
                <th style={{ width: 220 }}>Рах. витрат</th>
              </tr>
            </thead>
            <tbody>
              {form.products.filter((p) => p.nomenclature_id).map((p) => {
                const i = form.products.indexOf(p);
                return (
                  <tr key={`p${i}`}>
                    <td>Продукція</td>
                    <td>{nomName(p.nomenclature_id)}</td>
                    <td>{acctSelect(p.account_code, (v) => setProductField(i, { account_code: v }), '26')}</td>
                    <td>{acctSelect(p.cost_account_code, (v) => setProductField(i, { cost_account_code: v }), '231')}</td>
                  </tr>
                );
              })}
              {form.materials.filter((m) => m.nomenclature_id).map((m) => {
                const i = form.materials.indexOf(m);
                return (
                  <tr key={`m${i}`}>
                    <td>Матеріали</td>
                    <td>{nomName(m.nomenclature_id)}</td>
                    <td>{acctSelect(m.account_code, (v) => setMaterialField(i, { account_code: v }), '201')}</td>
                    <td />
                  </tr>
                );
              })}
              {form.returnable_waste.filter((w) => w.nomenclature_id).map((w) => {
                const i = form.returnable_waste.indexOf(w);
                return (
                  <tr key={`w${i}`}>
                    <td>Відходи</td>
                    <td>{nomName(w.nomenclature_id)}</td>
                    <td>{acctSelect(w.account_code, (v) => setWasteField(i, { account_code: v }), '201')}</td>
                    <td />
                  </tr>
                );
              })}
            </tbody>
          </TchTable>
          <p style={{ color: 'var(--muted)', fontSize: '0.78rem', marginTop: '0.4rem' }}>
            Порожньо — рахунок за замовчуванням (вказаний у дужках). Оберіть свій, щоб перебити.
          </p>
        </div>
      )}

      {tab === 'products' && (
        <div style={{ marginTop: '0.75rem' }}>
          <TchTable>
            <thead>
              <tr>
                <th style={{ width: 32 }}>#</th>
                <th>Продукція</th>
                <th style={{ width: 90 }}>К-сть</th>
                <th style={{ width: 80 }}>Одиниця</th>
                <th style={{ width: 170 }}>Номенкл. група</th>
                {isWip && <th style={{ width: 100 }}>Планова ціна</th>}
                {isWip && <th style={{ width: 100 }}>Планова сума</th>}
                <th style={{ width: 32 }} />
              </tr>
            </thead>
            <tbody>
              {form.products.map((p, i) => (
                <tr key={i}>
                  <td>{p.line_number}</td>
                  <td>
                    <NomenclatureSelect value={p.nomenclature_id}
                      onChange={(v) => setProductField(i, { nomenclature_id: v })} disabled={isPosted} kind="goods" />
                  </td>
                  <td><input type="number" step="0.001" value={p.quantity} onChange={(e) => setProductField(i, { quantity: e.target.value })} disabled={isPosted} /></td>
                  <td style={{ color: 'var(--muted)' }}>{uomOf(p.nomenclature_id, p.uom_id)}</td>
                  <td>
                    <select value={p.nomenclature_group_id ?? ''} onChange={(e) => setProductField(i, { nomenclature_group_id: e.target.value || null })} disabled={isPosted}>
                      <option value="">—</option>
                      {groupQ.data?.map((g) => (<option key={g.id} value={g.id}>{g.name}</option>))}
                    </select>
                  </td>
                  {isWip && <td><input type="number" step="0.01" value={p.planned_cost ?? ''} onChange={(e) => setProductField(i, { planned_cost: e.target.value || null })} disabled={isPosted} /></td>}
                  {isWip && <td><input type="number" step="0.01" value={p.planned_amount ?? ''} onChange={(e) => setProductField(i, { planned_amount: e.target.value || null })} disabled={isPosted} /></td>}
                  <td>{!isPosted && <button onClick={() => removeProduct(i)} title="Видалити рядок">×</button>}</td>
                </tr>
              ))}
            </tbody>
          </TchTable>
        </div>
      )}

      {tab === 'materials' && (
        <div style={{ marginTop: '0.75rem' }}>
          <TchTable>
            <thead>
              <tr>
                <th style={{ width: 32 }}>#</th>
                <th>Сировина</th>
                <th style={{ width: 90 }}>К-сть</th>
                <th style={{ width: 80 }}>Одиниця</th>
                <th style={{ width: 32 }} />
              </tr>
            </thead>
            <tbody>
              {form.materials.map((m, i) => (
                <tr key={i}>
                  <td>{m.line_number}</td>
                  <td>
                    <NomenclatureSelect value={m.nomenclature_id}
                      onChange={(v) => setMaterialField(i, { nomenclature_id: v })} disabled={isPosted} kind="goods" />
                  </td>
                  <td><input type="number" step="0.001" value={m.quantity} onChange={(e) => setMaterialField(i, { quantity: e.target.value })} disabled={isPosted} /></td>
                  <td style={{ color: 'var(--muted)' }}>{uomOf(m.nomenclature_id, m.uom_id)}</td>
                  <td>{!isPosted && <button onClick={() => removeMaterial(i)} title="Видалити рядок">×</button>}</td>
                </tr>
              ))}
            </tbody>
          </TchTable>
        </div>
      )}

      {tab === 'waste' && (
        <div style={{ marginTop: '0.75rem' }}>
          <div style={{ color: 'var(--muted)', fontSize: '0.8rem', marginBottom: '0.5rem' }}>
            Поворотні відходи повертаються на склад і зменшують собівартість продукції на вказану суму.
          </div>
          <TchTable>
            <thead>
              <tr>
                <th style={{ width: 32 }}>#</th>
                <th>Відходи</th>
                <th style={{ width: 90 }}>К-сть</th>
                <th style={{ width: 80 }}>Одиниця</th>
                <th style={{ width: 90 }}>Ціна</th>
                <th style={{ width: 90 }}>Сума</th>
                <th style={{ width: 32 }} />
              </tr>
            </thead>
            <tbody>
              {form.returnable_waste.map((w, i) => (
                <tr key={i}>
                  <td>{w.line_number}</td>
                  <td>
                    <NomenclatureSelect value={w.nomenclature_id}
                      onChange={(v) => setWasteField(i, { nomenclature_id: v })} disabled={isPosted} kind="goods" />
                  </td>
                  <td><input type="number" step="0.001" value={w.quantity} onChange={(e) => setWasteField(i, { quantity: e.target.value })} disabled={isPosted} /></td>
                  <td style={{ color: 'var(--muted)' }}>{uomOf(w.nomenclature_id)}</td>
                  <td><input type="number" step="0.01" value={w.price ?? ''} onChange={(e) => setWasteField(i, { price: e.target.value || null })} disabled={isPosted} /></td>
                  <td><input type="number" step="0.01" value={w.amount} onChange={(e) => setWasteField(i, { amount: e.target.value })} disabled={isPosted} /></td>
                  <td>{!isPosted && <button onClick={() => removeWaste(i)} title="Видалити рядок">×</button>}</td>
                </tr>
              ))}
            </tbody>
          </TchTable>
        </div>
      )}

      {tab === 'services' && (
        <div style={{ marginTop: '0.75rem' }}>
          <TchTable>
            <thead>
              <tr>
                <th style={{ width: 32 }}>#</th>
                <th>Послуга</th>
                <th style={{ width: 90 }}>К-сть</th>
                <th style={{ width: 100 }}>Сума</th>
                <th style={{ width: 170 }}>Номенкл. група</th>
                <th>Коментар</th>
                <th style={{ width: 32 }} />
              </tr>
            </thead>
            <tbody>
              {form.services.map((s, i) => (
                <tr key={i}>
                  <td>{s.line_number}</td>
                  <td>
                    <NomenclatureSelect value={s.nomenclature_id}
                      onChange={(v) => setServiceField(i, { nomenclature_id: v })} disabled={isPosted} kind="services" />
                  </td>
                  <td><input type="number" step="0.001" value={s.quantity} onChange={(e) => setServiceField(i, { quantity: e.target.value })} disabled={isPosted} /></td>
                  <td><input type="number" step="0.01" value={s.amount ?? ''} onChange={(e) => setServiceField(i, { amount: e.target.value || null })} disabled={isPosted} /></td>
                  <td>
                    <select value={s.nomenclature_group_id ?? ''} onChange={(e) => setServiceField(i, { nomenclature_group_id: e.target.value || null })} disabled={isPosted}>
                      <option value="">—</option>
                      {groupQ.data?.map((g) => (<option key={g.id} value={g.id}>{g.name}</option>))}
                    </select>
                  </td>
                  <td><input value={s.comment ?? ''} onChange={(e) => setServiceField(i, { comment: e.target.value || null })} disabled={isPosted} /></td>
                  <td>{!isPosted && <button onClick={() => removeService(i)} title="Видалити рядок">×</button>}</td>
                </tr>
              ))}
            </tbody>
          </TchTable>
          <p style={{ color: 'var(--muted)', fontSize: '0.78rem', marginTop: '0.4rem' }}>
            Внутрішні послуги виробничого характеру (1С «Услуги»). Зберігаються довідково — у проведенні участі не беруть.
          </p>
        </div>
      )}

      {pickerFor && form.organization_id && (
        <NomenclaturePickerModal
          organizationId={form.organization_id}
          documentDate={form.document_date}
          defaultWarehouseId={form.warehouse_id || null}
          catalogMode
          defaultOnlyWithStock={pickerFor === 'materials'}
          onlyKind="goods"
          multiSelect
          onPick={(row) => addRowsFromPicker([row])}
          onPickMany={addRowsFromPicker}
          onClose={() => setPickerFor(null)}
        />
      )}

      </DocBlock>

      <DocBlock index={4} title="Коментарі та вкладення">
      <div className="docblock__cols">
        <div className="field" style={{ margin: 0 }}>
          <label>Коментар</label>
          <input value={form.comment}
                 onChange={(e) => setForm({ ...form, comment: e.target.value })} disabled={isPosted} />
        </div>
        <div>
          {/* Вкладення (2026-08-13, рішення власника: на всіх документах). */}
          <AttachmentsPanel organizationId={form.organization_id}
            documentType="doc.production_reports" documentId={id} />
        </div>
      </div>
      </DocBlock>
      </DocLayout>
      {error && <div className="error" style={{ marginTop: '1rem' }}>{error}</div>}
    </div>
  );
}
